'use client';
import { Roboto } from 'next/font/google';
import { createTheme } from '@mui/material/styles';
import { fontSize } from '@mui/system';

const roboto = Roboto({
  weight: ['300', '400', '500', '700'],
  subsets: ['latin'],
  display: 'swap'
});

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 767,
      md: 1050,
      lg: 1200,
      xl: 1536
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#F4A71C',
      light: '#F8C15D',
      dark: '#D18F19',
      contrastText: '#1C1C1C'
    },
    action: {
      selected: '#FFF5E0',
      disabled: 'rgba(28, 28, 28,0.38)',
      disabledBackground: 'rgba(29, 27, 32, 0.12)'
    },
    secondary: {
      main: '#1C1C1C',
      light: '#ADADAD',
      dark: '#7C7C7C',
      contrastText: '#959595'
    },
    background: {
      default: '#f5f5f5'
    },
    yellow: {
      lightest: '#FFF5E0',
      light: '#FBD89F',
      main: '#F8C15D',
      dark: '#F4A71C'
    },
    success: {
      main: '#BDEAF2',
      light: '#BDEAF2',
      dark: '#BDEAF2',
      contrastText: '#1C1C1C'
    },
    info: {
      main: '#60a5fa',
      light: '#BDEAF2',
      contrastText: '#1C1C1C'
    }
    // error: {
    //   main: "#FFDAD6",
    //   contrastText: "#410002",
    // },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontSize: 14
  },
  shape: {
    borderRadius: 4
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: ({ theme }) => ({
          fontFamily: 'Scharf',
          fontWeight: 400,
          fontSize: '32px',
          lineHeight: '40px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '24px'
          }
        }),
        h2: ({ theme }) => ({
          fontFamily: 'Scharf',
          fontWeight: 500,
          fontSize: '24px',
          lineHeight: '32px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '20px'
          }
        }),
        h3: ({ theme }) => ({
          fontFamily: 'Scharf',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '32px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '18px'
          }
        }),
        h4: () => ({
          fontFamily: 'Oswald-Regular',
          fontWeight: 500,
          fontSize: '14px'
        }),
        body1: () => ({
          fontFamily: 'Droid Serif',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px'
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 0,
          boxShadow: 'none',
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          [theme.breakpoints.down('md')]: {
            padding: '30px'
          },
          [theme.breakpoints.down('sm')]: {
            padding: '20px'
          }
        })
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            borderRadius: '6px',
            borderColor: '#959595'
          }
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            gap: '25px',
            border: '2px solid var(--secondary-contrast-text, #f4a71c)',
            background: 'var(--Primary-Yellow-99, #fffdf7)'
          }
        }
      ]
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '25px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '0',
          marginLeft: '0'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Scharf',
          padding: '0',
          marginLeft: '0',
          lineHeight: '28px',
          fontSize: '22px',
          fontWeight: '500'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0',
          marginLeft: '0'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: () => ({
          borderRadius: 8,
          padding: '10px 24px',
          minWidth: '88px',
          lineHeight: '1.4285',
          textTransform: 'none',
          fontFamily: 'Oswald-Regular',
          fontWeight: 400,
          fontSize: '16px'
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#959595'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.focused) {
            return {
              '&.Mui-focused': {
                color: '#1C1C1C'
              }
            };
          }
          return {
            color: '#1C1C1C'
          };
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px'
        }
      }
      // styleOverrides: {
      //   root: ({ ownerState }) => ({
      //     borderRadius: '4px',
      //     ...(ownerState.severity === 'info' && {
      //       backgroundColor: '#60a5fa',
      //       color: '#1C1C1C'
      //     }),
      //     ...(ownerState.severity === 'success' && {
      //       backgroundColor: '#BDEAF2',
      //       color: '#1C1C1C'
      //     }),
      //     ...(ownerState.severity === 'error' && {
      //       backgroundColor: '#FFDAD6',
      //       color: '#410002'
      //     })
      //   })
      // }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '25px'
          // borderColor: "var(--Secondary-Grey-80, #C5C5C5)",
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          padding: '0px'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#C5C5C5',
          width: '100%'
        }
      }
    },
    MuiGrid: {
      defaultProps: {
        spacing: 3
      }
    },
    MuiStack: {
      defaultProps: {
        spacing: 3
      }
    }
    // MuiTypography: {
    //   defaultProps: {
    //     variantMapping: {
    //       h1: 'h2',
    //       h2: 'h2',
    //       h3: 'h2',
    //       h4: 'h2',
    //       h5: 'h2',
    //       h6: 'h2',
    //       subtitle1: 'h2',
    //       subtitle2: 'h2',
    //       body1: 'span',
    //       body2: 'p'
    //     }
    //   }
    // }
  }
});

export default theme;
